import { useEffect, useState } from 'react'
import { FilePlus2, Loader2, ListPlus } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useMaterial } from '@/hooks/useInventoryMaster'
import {
  useAddPurchaseOrderLine,
  useCreatePurchaseOrder,
  usePoLogo,
  usePoPrefill,
  usePoTemplate,
  usePurchaseOrders,
} from '@/hooks/usePurchaseOrders'
import { toReorderNumber } from '@/lib/reorder'
import { formatMoney } from '@/lib/poTotals'
import {
  addDaysIso,
  draftFromPrefill,
  draftProblem,
  draftToPayload,
  prefillLineInput,
  type PoDraft,
} from '@/lib/purchaseOrderDraft'
import { PurchaseOrderForm } from './PurchaseOrderForm'
import { PurchaseOrderDetailDialog } from './PurchaseOrderDetailDialog'

interface CreatePurchaseOrderDialogProps {
  /** The material to order (database id); null closes the dialog. */
  materialId: string | null
  /** The quantity settled in the re-order calculation; the server's suggestion when left out. */
  qty?: number
  onClose: () => void
}

/**
 * Raise a purchase order for one material (z8nrz7f7dz), pre-filled from the
 * server's proposal: the supplier, and a line for the suggested quantity.
 *
 * When that supplier already has a DRAFT order open, the planner chooses
 * between adding this material to it and starting a new one — two drafts to
 * the same supplier usually means one of them gets forgotten. Either way the
 * resulting order opens straight after, so it can be reviewed and sent.
 */
export function CreatePurchaseOrderDialog({ materialId, qty, onClose }: CreatePurchaseOrderDialogProps) {
  const prefill = usePoPrefill(materialId)
  // The order's line for this material, at the quantity the calculation settled on.
  const line = prefill.data && (qty !== undefined ? { ...prefill.data.line, qty } : prefill.data.line)
  const material = useMaterial(materialId)
  const supplier = prefill.data?.supplierName ?? ''
  const drafts = usePurchaseOrders({ status: 'draft', supplier }, { enabled: !!supplier })
  const create = useCreatePurchaseOrder()
  // A new order is a draft, so it fills in the CURRENT template's custom fields.
  const customFieldDefs = usePoTemplate().data?.layout.customFields ?? []
  // The tenant's logo heads the dialog, so the order reads as the branded document it becomes.
  const logoUrl = usePoLogo(usePoTemplate().data?.layout.branding.logoAssetKey)
  const addLine = useAddPurchaseOrderLine()

  const [mode, setMode] = useState<'choose' | 'edit'>('choose')
  const [draft, setDraft] = useState<PoDraft | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openedId, setOpenedId] = useState<string | null>(null)

  // Exact supplier only, whatever kind of match the server's filter makes.
  const openDrafts = (drafts.data?.items ?? []).filter(
    (d) => d.status === 'DRAFT' && d.supplierName.trim().toLowerCase() === supplier.trim().toLowerCase(),
  )
  // Wait for this mount's own lookup of the supplier's drafts to SUCCEED:
  // `isLoading` is false while cached data refetches, and a failed refetch
  // keeps the old data — either way a stale empty list would skip the "add to
  // the open draft" choice and let a second draft be raised. A failed lookup
  // blocks creation and offers a retry instead.
  const draftsChecked = !supplier || (drafts.isFetchedAfterMount && drafts.isSuccess)
  const draftsFailed = !!supplier && drafts.isError
  const ready = !!prefill.data && !!material.data && draftsChecked

  useEffect(() => {
    setMode('choose')
    setDraft(null)
    setError(null)
  }, [materialId, qty])

  // Seeded once both reads are in, never again: a refetch must not overwrite
  // what the planner has typed. Delivery is the server's date when the prefill
  // has one; this today-plus-lead-time is only the fallback.
  useEffect(() => {
    if (draft || !prefill.data || !material.data) return
    const attrs = material.data.attributes as Record<string, { value?: unknown }> | undefined
    const lead = toReorderNumber(attrs?.orderLeadTimeDays?.value) ?? 0
    setDraft(draftFromPrefill(line ? { ...prefill.data, line } : prefill.data, addDaysIso(new Date(), Math.max(0, lead))))
  }, [draft, prefill.data, material.data, line])

  const onError = (e: unknown) => setError(e instanceof Error ? e.message : 'The order could not be saved.')
  const finish = (id: string) => {
    setOpenedId(id)
    onClose()
  }

  function submit() {
    if (!draft) return
    const problem = draftProblem(draft)
    if (problem) return setError(problem)
    setError(null)
    create.mutate(draftToPayload(draft, customFieldDefs), { onSuccess: (po) => finish(po.id), onError })
  }

  function addToDraft(id: string) {
    if (!prefill.data) return
    setError(null)
    addLine.mutate({ id, line: prefillLineInput(line ?? prefill.data.line) }, { onSuccess: (po) => finish(po.id), onError })
  }

  const choosing = mode === 'choose' && openDrafts.length > 0
  const busy = create.isPending || addLine.isPending

  const cancelButton = (
    <Button variant='outline' onClick={onClose}>
      Cancel
    </Button>
  )

  return (
    <>
      <Dialog open={materialId !== null} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className='flex max-h-[94vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-7xl'>
          <DialogHeader className='space-y-1 border-b border-slate-200 bg-white px-6 py-4 pr-12 text-left'>
            <div className='flex flex-wrap items-center gap-2'>
              {logoUrl && <img src={logoUrl} alt='' className='mr-1 max-h-8 max-w-32 object-contain' />}
              <DialogTitle className='text-lg font-semibold text-slate-900'>New purchase order</DialogTitle>
              <span className='inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600'>
                Draft
              </span>
            </div>
            <DialogDescription className='flex flex-wrap items-center gap-x-2 text-xs text-slate-500'>
              <span>PO number assigned on save</span>
              {material.data && (
                <>
                  <span aria-hidden className='text-slate-300'>
                    •
                  </span>
                  <span>
                    <span className='font-mono text-slate-700'>{material.data.materialId}</span> ·{' '}
                    {material.data.materialName}
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {prefill.error ? (
            <div className='px-6 py-8'>
              <p className='text-sm text-destructive'>
                {prefill.error instanceof Error ? prefill.error.message : 'Could not prepare the order.'}
              </p>
            </div>
          ) : draftsFailed ? (
            <div className='space-y-3 px-6 py-8'>
              <p className='text-sm text-destructive'>
                Could not check {supplier}'s open draft orders, so a new order cannot be started yet (it might
                duplicate one).
              </p>
              <Button variant='outline' size='sm' onClick={() => void drafts.refetch()} disabled={drafts.isFetching}>
                {drafts.isFetching ? 'Checking…' : 'Retry'}
              </Button>
            </div>
          ) : !ready || !draft ? (
            <div className='flex items-center gap-2 px-6 py-10 text-sm text-slate-500'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Preparing the order…
            </div>
          ) : choosing ? (
            <>
              <div className='min-h-0 flex-1 overflow-y-auto bg-slate-50 px-6 py-5'>
                <div className='mx-auto max-w-3xl space-y-4'>
                  <div>
                    <h3 className='text-xs font-semibold uppercase tracking-wide text-slate-500'>Open drafts for this vendor</h3>
                    <p className='mt-1 text-sm text-slate-700'>
                      {supplier} already has {openDrafts.length === 1 ? 'a draft order' : 'draft orders'} open.
                      Add {(line?.qty ?? 0).toLocaleString('en-US')} of this material to one, or start a new
                      order?
                    </p>
                  </div>
                  <div className='grid gap-3 sm:grid-cols-2'>
                    {openDrafts.map((d) => (
                      <button
                        key={d.id}
                        type='button'
                        disabled={busy}
                        onClick={() => addToDraft(d.id)}
                        className='flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-primary hover:bg-primary/[0.03] disabled:opacity-50'
                      >
                        <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10'>
                          <ListPlus className='h-5 w-5 text-primary' />
                        </span>
                        <span className='min-w-0'>
                          <span className='block font-medium text-slate-900'>Add to draft {d.number}</span>
                          <span className='block text-xs tabular-nums text-slate-500'>
                            {d.lineCount} line{d.lineCount === 1 ? '' : 's'} · {formatMoney(d.grandTotal, d.currency)}
                          </span>
                        </span>
                      </button>
                    ))}
                    <button
                      type='button'
                      disabled={busy}
                      onClick={() => setMode('edit')}
                      className='flex items-start gap-3 rounded-xl border border-dashed border-slate-300 bg-white p-4 text-left transition-colors hover:border-primary hover:bg-primary/[0.03] disabled:opacity-50'
                    >
                      <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100'>
                        <FilePlus2 className='h-5 w-5 text-slate-600' />
                      </span>
                      <span className='min-w-0'>
                        <span className='block font-medium text-slate-900'>New purchase order</span>
                        <span className='block text-xs text-slate-500'>A separate order to {supplier}</span>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              <div className='flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-white px-6 py-3'>
                {error && <p className='mr-auto text-sm text-destructive'>{error}</p>}
                {cancelButton}
              </div>
            </>
          ) : (
            <>
              <div className='min-h-0 flex-1 overflow-y-auto bg-slate-50 px-6 py-5'>
                <PurchaseOrderForm
                  draft={draft}
                  onChange={setDraft}
                  customFieldDefs={customFieldDefs}
                />
              </div>
              <div className='flex flex-wrap items-center justify-end gap-3 border-t border-slate-200 bg-white px-6 py-3'>
                {error && <p className='mr-auto text-sm text-destructive'>{error}</p>}
                {cancelButton}
                <Button onClick={submit} disabled={busy}>
                  {create.isPending ? 'Saving…' : 'Save draft'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <PurchaseOrderDetailDialog poId={openedId} onClose={() => setOpenedId(null)} />
    </>
  )
}
